// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

const NF = new Intl.NumberFormat('en-AU');

export function num(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return NF.format(Math.round(n));
}

/**
 * Money, at the precision a reader can actually use.
 *
 * Every dollar figure on this site is a per-farm average from a sample survey.
 * Printing $12,016,060 implies a precision the survey does not have, so above a
 * million it rounds to three significant figures ("$12.0m") and the exact value
 * stays in the tooltip for anyone who wants it.
 */
export function money(n: number | null | undefined, opts: { long?: boolean } = {}): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (opts.long) return `${n < 0 ? '-' : ''}$${NF.format(Math.abs(Math.round(n)))}`;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}m`;
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}k`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${NF.format(Math.round(abs))}`;
}

export function pct(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
}

/** A signed percentage, where the sign is the point (returns, changes). */
export function signedPct(n: number | null | undefined, dp = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(dp)}%`;
}

export function ha(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${NF.format(Math.round(n))} ha`;
}

/**
 * A financial year, said the way ABARES means it.
 *
 * The `Year` column is the year the financial year ENDS — 2024 is 2023-24. A
 * site that prints a bare "2024" invites every reader to compare it against a
 * calendar-year figure from somewhere else.
 */
export function fy(year: number | null | undefined): string {
  if (year == null || !Number.isFinite(year)) return '—';
  return `${year - 1}–${String(year).slice(2)}`;
}

export function esc(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Truncate on a word boundary, then escape. Never truncate escaped text. */
export function truncEsc(s: string | null | undefined, max: number): string {
  const raw = String(s ?? '');
  if (raw.length <= max) return esc(raw);
  const cut = raw.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `${esc(sp > max * 0.6 ? cut.slice(0, sp) : cut)}…`;
}

/**
 * Text for the `data-tip` ATTRIBUTE, set via setAttribute.
 *
 * setAttribute takes RAW newlines — the CSS `white-space: pre-line` on the
 * tooltip renders them. Passing `&#10;` here would print the literal characters
 * "&#10;" to the user; that is only correct when building an HTML string.
 */
export function tip(...lines: (string | null | undefined | false)[]): string {
  return lines.filter(Boolean).join('\n');
}

/** The same tooltip text, for embedding in an innerHTML string. */
export function tipAttr(...lines: (string | null | undefined | false)[]): string {
  return esc(lines.filter(Boolean).join('\n')).replace(/\n/g, '&#10;');
}

export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
